import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {  motion } from 'framer-motion';
import { Toaster, toast } from 'sonner';
import { FaChevronRight, FaChevronLeft, FaCheck } from 'react-icons/fa';
import { BiSolidBank } from 'react-icons/bi';

/* =========================
   UTILS
   ========================= */

const FormContext = React.createContext(null);
const StepContext = React.createContext(1);

const getValue = (obj, path) => {
  if (typeof path !== "string" || !path.length) {
    console.error("Invalid path passed to getValue:", path);
    return undefined;
  }

  return path.split(".").reduce((acc, part) => {
    if (acc === undefined || acc === null) {
      return undefined;
    }

    if (!isNaN(part)) {
      return acc?.[Number(part)];
    }

    return acc?.[part];
  }, obj);
};

  const generatePayload = (mode, data, paths = []) => {
  if (!data || typeof data !== "object") return data;

  const getValue = (obj, path) => {
  if (typeof path !== "string" || !path.length) {
    console.error("Invalid path passed to generatePayload.getValue:", path);
    return undefined;
  }

  return path.split(".").reduce((acc, key) => acc?.[key], obj);
};

  const setValue = (obj, path, value) => {
  if (typeof path !== "string" || !path.length) {
    console.error("Invalid path passed to generatePayload.setValue:", path);
    return;
  }

  const parts = path.split(".");
  let current = obj;

  parts.forEach((key, index) => {
    if (index === parts.length - 1) {
      current[key] = value;
    } else {
      if (!current[key]) current[key] = {};
      current = current[key];
    }
  });
};

  const deletePath = (obj, path) => {
  if (typeof path !== "string" || !path.length) {
    console.error("Invalid path passed to generatePayload.deletePath:", path);
    return;
  }

  const parts = path.split(".");
  let current = obj;

  for (let i = 0; i < parts.length - 1; i++) {
    if (!current?.[parts[i]]) return;
    current = current[parts[i]];
  }

  delete current[parts[parts.length - 1]];
};

  //  KEEP MODE
  if (mode === "keep") {
    const result = {};

    paths.forEach(path => {
      const val = getValue(data, path);
      if (val !== undefined) {
        setValue(result, path, val);
      }
    });

    return result;
  }

  //  REMOVE MODE
  if (mode === "remove") {
    const result = JSON.parse(JSON.stringify(data));

    paths.forEach(path => {
      deletePath(result, path);
    });

    return result;
  }

  return data;
};

const setDeepValue = (data, path, value) => {
  if (typeof path !== "string" || !path.length) {
    console.error("Invalid path passed to setDeepValue:", path);
    return data;
  }

  const parts = path.split(".");
  const result = JSON.parse(JSON.stringify(data || {}));

  let current = result;

  parts.forEach((key, index) => {
    const isLast = index === parts.length - 1;
    const nextKey = parts[index + 1];

    if (!isNaN(key)) {
      const i = Number(key);

      if (!Array.isArray(current)) {
        return;
      }

      if (!current[i]) current[i] = {};

      if (isLast) {
        current[i] = value;
      } else {
        current = current[i];
      }
    } else {
      if (isLast) {
        current[key] = value;
      } else {
        if (!current[key]) {
          current[key] = !isNaN(nextKey) ? [] : {};
        }

        current = current[key];
      }
    }
  });

  return result;
};

const toISO = (value) => {
  if (typeof value !== "string") {
    console.error("Invalid date passed to toISO:", value);
    return "";
  }

  const parts = value.split("-");

  if (parts.length !== 3) {
    console.error("Unexpected date format in toISO:", value);
    return "";
  }

  const [day, month, year] = parts;

  return `${year}-${month}-${day}`;
};

const getOrCreateApplicationId = () => {
  const KEY = "applicationId";

  const existing = sessionStorage.getItem(KEY);

  // UUID v4 regex
  const uuidV4Regex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

  if (existing && uuidV4Regex.test(existing)) {
    return existing;
  }

  // generate new UUID v4
  const newId = crypto.randomUUID();

  sessionStorage.setItem(KEY, newId);

  return newId;
};

/* =========================
   HELPERS
   ========================= */

function formatPhone(value) {
  if (!value) return "";
  return value
    .replace(/\D/g, "")
    .replace(/^(\d{3})(\d{3})(\d{4})$/, "$1 $2 $3");
}

function validatePhone(value) {
  if (!value) return false;
  const digits = value.replace(/[^0-9]/g, "");
  return digits.length === 10;
}

const transformDate = (value) => {
  if (!value) return "";

  // allow only digits + separators
  // eslint-disable-next-line
  const cleaned = value.replace(/[^0-9\/\-.]/g, "");

  // detect separator
  const separator = cleaned.includes("/")
    ? "/"
    : cleaned.includes("-")
    ? "-"
    : cleaned.includes(".")
    ? "."
    : null;

  if (!separator) return cleaned; // ❌ no separator → do nothing

  const parts = cleaned.split(separator);

  if (parts.length !== 3) return cleaned;

  let year, month, day;

  // YYYY first
  if (parts[0].length === 4) {
    [year, month, day] = parts;
  } else {
    // DD first
    [day, month, year] = parts;
  }

  if (year.length !== 4 || month.length !== 2 || day.length !== 2) {
    return cleaned;
  }

  // return `${year}-${month}-${day}`; //ISO DATE FORMAT
  return `${day}-${month}-${year}`; 
};

const validateDate = (value) => {
  if (!value) return false;

  const match = value.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (!match) return false;

  const [, d, m, y] = match;

  const year = Number(y);
  const month = Number(m);
  const day = Number(d);

  // basic sanity
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;

  // build date safely (no timezone issues)
  const dob = new Date(year, month - 1, day);
  const today = new Date();

  // calculate age
  let age = today.getFullYear() - dob.getFullYear();
  const hasHadBirthday =
    today.getMonth() > dob.getMonth() ||
    (today.getMonth() === dob.getMonth() &&
      today.getDate() >= dob.getDate());

  if (!hasHadBirthday) age--;

  return age >= 18;
};

const validateDateValue = (value) => {
  if (!value) return false;

  const match = value.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (!match) return false;

  const [, d, m, y] = match;

  const year = Number(y);
  const month = Number(m);
  const day = Number(d);

  // basic sanity
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;
  if (year > new Date().getFullYear()) return false;

  return true;
};



/* =========================
   INPUTS
   ========================= */

const FormInput = ({
  label,
  path,
  formData,
  setFormData,
  required = false,
  type = "text",
  placeholder,
  transform,
  defaultValue,
  validate,
  ...props
}) => {
  const { registerField, setInvalidFields } = React.useContext(FormContext);
  const step = React.useContext(StepContext);

  const rawValue = getValue(formData, path);
  const value = rawValue ?? defaultValue ?? "";

  // set default into formData once
// eslint-disable-next-line react-hooks/exhaustive-deps
React.useEffect(() => {
  if (defaultValue === undefined) return;

  const existing = getValue(formData, path);
  if (existing !== undefined && existing !== "") return;

  setFormData(prev =>
    setDeepValue(prev, path, defaultValue)
  );
  // eslint-disable-next-line
}, [defaultValue, path]);

  // register field
  React.useEffect(() => {
    registerField(path, { required, label, step });
  }, [path, required, label, step, registerField]);

  const handleChange = (e) => {
    let val = e.target.value;
    if (typeof path !== "string") {
  console.error("Invalid path in FormSelect:", path);
  return;
}
    if (transform) val = transform(val);

    setFormData(prev => setDeepValue(prev, path, val));
  };

  const handleBlur = () => {
    if (!validate) return;

    const isValid = validate(value);

    setInvalidFields(prev => {
      const newMap = new Map(prev);

      if (!value || isValid) {
        newMap.delete(path);
      } else {
        newMap.set(path, { label, step });
      }

      return newMap;
    });

    if (value && !isValid) {
      toast.error(`Invalid input for ${label}`);
    }
  };

  return (
    <div className="w-full text-left">
      {label && (
        <label className="block text-gray-700 font-medium mb-2">
          {label}
          {required && <span className="ml-1 font-bold">*</span>}
        </label>
      )}

      <input
        name={path}
        {...props}
        type={type}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        className={`w-full px-4 py-3 rounded-xl border-2 transition-all duration-300 outline-none
          ${
            value
              ? "border-primary-700 bg-green-50/10"
              : "border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20"
          }`}
      />
    </div>
  );
};

const FormSelect = ({
  label,
  path,
  formData,
  setFormData,
  options,
  className = "",
  onChangeOverride,
  transform,
  validate,
  required = false
}) => {
  const { registerField } = React.useContext(FormContext);
  const step = React.useContext(StepContext);

  const value = getValue(formData, path) || "";

  React.useEffect(() => {
    registerField(path, { required, label, step });
  }, [path, required, label, step, registerField]);

  const handleChange = (val) => {
    if (typeof path !== "string") {
  console.error("Invalid path in FormSelect:", path);
  return;
}
    if (validate && !validate(val)) return;

    const finalVal = transform ? transform(val) : val;

    if (onChangeOverride) {
      onChangeOverride(finalVal);
      return;
    }

    setFormData(prev => {
      const newForm = { ...prev };
      const parts = path.split('.');
      let current = newForm;

      for (let i = 0; i < parts.length - 1; i++) {
        if (!current[parts[i]]) current[parts[i]] = {};
        current = current[parts[i]];
      }

      current[parts[parts.length - 1]] = finalVal;
      return newForm;
    });
  };

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <label className="block text-gray-700 font-medium mb-2 text-left">
          {label}
          {required && <span className=" ml-1 font-bold">*</span>}
        </label>
      )}

      <div className="relative">
        <select
          name={path}
          className={`w-full px-4 py-3 pr-12 rounded-xl border-2 transition-all duration-300 bg-white appearance-none cursor-pointer outline-none
            ${value ? 'border-primary-700' : 'border-gray-200 focus:border-primary'}`}
          value={value}
          onChange={(e) => handleChange(e.target.value)}
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value} disabled={opt.disabled}>
              {opt.label}
            </option>
          ))}
        </select>

        <div className="pointer-events-none absolute inset-y-0 right-4 flex items-center text-gray-400">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
    </div>
  );
};

const FormExpense = ({
  label,
  fieldKey,
  formData,
  setFormData,
  required = false
}) => {
  const { registerField } = React.useContext(FormContext);
  const step = React.useContext(StepContext);

  const path = `consumerProvidedIncomeDetails.${fieldKey}.value`;
  const value = getValue(formData, path) || "";

  React.useEffect(() => {
    registerField(path, { required, label, step });
  }, [path, label, step, required, registerField]);

  const data =
    formData.consumerProvidedIncomeDetails?.[fieldKey] || {
      value: "",
      frequency: "Weekly"
    };

  const update = (key, val) => {
    setFormData(prev => ({
      ...prev,
      consumerProvidedIncomeDetails: {
        ...(prev.consumerProvidedIncomeDetails || {}),
        [fieldKey]: {
          ...(prev.consumerProvidedIncomeDetails?.[fieldKey] || {
            frequency: "Weekly"
          }),
          [key]: val
        }
      }
    }));
  };

  return (
    <div>
      <label className="block text-gray-700 font-semibold mb-2 text-left">
        {label}
        {required && <span className=" ml-1 font-bold">*</span>}
      </label>

      <div className="flex gap-4">
        <div className="relative flex-1">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">$</span>
          <input
            type="number"
            className={`w-full pl-8 pr-4 py-3 rounded-xl border-2 outline-none transition-all duration-300
              ${value ? 'border-primary-700 bg-green-50/10' : 'border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 bg-white'}`}
            value={data.value}
            onChange={(e) => update("value", e.target.value)}
          />
        </div>

        <div className="relative flex-1">
          <select
            className={`w-full px-4 py-3 pr-12 rounded-xl border-2 transition-all duration-300 bg-white appearance-none cursor-pointer outline-none
              border-gray-200 focus:border-primary`}
            value={data.frequency}
            onChange={(e) => update("frequency", e.target.value)}
          >
            <option value="Weekly">Weekly</option>
            <option value="Fortnightly">Fortnightly</option>
            <option value="Monthly">Monthly</option>
          </select>

          <div className="pointer-events-none absolute inset-y-0 right-4 flex items-center text-gray-400">
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

/* =========================
   MAIN
   ========================= */

const ApplicationForm = () => {

  // eslint-disable-next-line
const navigate = useNavigate();
const [showBankModal, setShowBankModal] = useState(false);
const [iframeUrl, setIframeUrl] = useState("");
const openBankModal = () => {
  const url = buildBankUrl();
    console.log(url);
  if (!url)
    return;

  setIframeUrl(url);
  setShowBankModal(true);
};
const closeBankModal = () => setShowBankModal(false);
const [formData, setFormData] = useState({});
const getFormValue = (path) => getValue(formData, path);

  const [requiredFields, setRequiredFields] = useState(new Map());
  const [invalidFields, setInvalidFields] = useState(new Set());

  const registerField = React.useCallback((path, config) => {
    setRequiredFields(prev => {
      const m = new Map(prev);
      if (config?.required) m.set(path, config);
      else m.delete(path);
      return m;
    });
  }, []);

  // eslint-disable-next-line
  const validateAll = () => {
    if (invalidFields.size > 0) {
      const first = Array.from(invalidFields)[0];
      const config = requiredFields.get(first);
      toast.error(`Invalid input for ${config?.label || "field"}`);
      if (config?.step) setStep(config.step);
      return false;
    }

    for (let [path, config] of requiredFields.entries()) {
      const val = getValue(formData, path);
      if (!val || String(val).trim() === "") {
        toast.error(`${config.label} is required`);
        if (config.step) setStep(config.step);
        return false;
      }
    }

    return true;
  };

  const sectionRef = useRef(null);
  const [step, setStep] = useState(1);
  const [note, setNote] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  // const [showCompanyDetails, setShowCompanyDetails] = useState(false);

  const [agreements, setAgreements] = useState({
    confirmAccuracy: false,
    confirmAuthorised: false,
    agreePrivacy: false,
    understandCreditInfo: false
  });

/*   const getAlpha2FromAlpha3 = (alpha3) => {
  if (!alpha3) return "";

  const country = foreignCountries.find(
    (c) => c["alpha-3"] === alpha3
  );

  return country ? country["alpha-2"] : "";
}; */

  const toggleAgreement = (key) =>
    setAgreements(prev => ({ ...prev, [key]: !prev[key] }));

  const allAgreementsChecked = Object.values(agreements).every(Boolean);

  const handleNextStep = () => {
    for (let [path, config] of requiredFields.entries()) {
      if (config.step !== step) continue;
      const val = getValue(formData, path);
      if (!val || String(val).trim() === "") {
        toast.error(`${config.label} is required`);
        return;
      }
    }
    setStep(prev => prev + 1);
    window.scrollTo({
      top: sectionRef.current?.offsetTop - 100,
      behavior: "smooth"
    });
  };

  const BASE_URL_TALEFIN = process.env.REACT_APP_BASE_URL_TALEFIN;
const VENDOR_REF = process.env.REACT_APP_VENDOR_REF;

const buildBankUrl = () => {
  const applicationId = getOrCreateApplicationId();

  const firstName = getFormValue("basicDetails.personalDetails.firstName");
  const lastName = getFormValue("basicDetails.personalDetails.lastName");
  const email = getFormValue("basicDetails.contactDetails.email");
  const phoneNumberRaw = getFormValue("basicDetails.contactDetails.phone.0.number");
  const phoneNumber = (phoneNumberRaw || "").replace(/\D/g, '');

  if (!firstName) {
    toast.error("First Name is required for Bank Statement Verification");
    return null;
  }

  if (!lastName) {
    toast.error("Last Name is required for Bank Statement Verification");
    return null;
  }

  if (!email) {
    toast.error("Email is required for Bank Statement Verification");
    return null;
  }

  if (!phoneNumber) {
    toast.error("Phone Number is required for Bank Statement Verification");
    return null;
  }

  const params = new URLSearchParams({
    firstName,
    lastName,
    email,
    phoneNumber,
    multiple: "true"
  });

  return `${BASE_URL_TALEFIN}/${VENDOR_REF}/${applicationId}/banks/?${params.toString()}`;
};

const hasCreatedRef = useRef(false);

const createBankStatement = async () => {

  try {
    const applicationId = getOrCreateApplicationId();
    const loanId = crypto.randomUUID();

    await fetch("/api/createBankStatement", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        loanId,
        applicationId
      })
    });
  } catch (err) {
    console.error("Failed to create bank statement", err);
  }
};

const submitApplication = async () => {
  try {
    let payload = setDeepValue(
      formData,
      "basicDetails.contactDetails.phone.0.type",
      "Mobile"
    );

    payload = setDeepValue(
      payload,
      "id",
      getOrCreateApplicationId()
    );

    payload = setDeepValue(payload,"loanDetails.type", "Personal Loan");
    payload = setDeepValue(payload,"basicDetails.personalDetails.dateOfBirth",toISO(getValue(payload, "basicDetails.personalDetails.dateOfBirth")));
    payload = setDeepValue(payload,"basicDetails.employmentDetails.employmentDate",toISO(getValue(payload, "basicDetails.employmentDetails.employmentDate")));

    payload = generatePayload("remove", payload, ["identificationDocs"]);

    payload = setDeepValue(
      payload,
      "notes",
      note
        ? [
            ...(getValue(payload, "notes") || []),
            { content: note, date: new Date().toISOString() }
          ]
        : (getValue(payload, "notes") || [])
    );

    //  Debug (optional)
    //console.log("Submitting:", payload);


    const res = await fetch("/api/submit", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(payload)
    });

    if (!res.ok) {
  let message = "Submission failed";

  try {
    const data = await res.json();
    message = data?.message || data?.error || message;
  } catch {
    
  }

  throw new Error(message);
}

    return true;
  } catch (err) {
    console.error(err);
    throw err;
  }
};

const handleFinalSubmit = async () => {
  // 1. Invalid fields first
if (invalidFields.size > 0) {
  const [firstPath, config] = Array.from(invalidFields.entries())[0];

  toast.error(`Invalid input for ${config?.label || firstPath}`);

  if (config?.step) {
    setStep(config.step);
    window.scrollTo({
      top: sectionRef.current?.offsetTop - 100,
      behavior: "smooth"
    });
  }

  return;
}

  // 2. Required fields
  for (let [path, config] of requiredFields) {
    if (!config.required) continue;

    const val = getValue(formData, path);

    if (!val || String(val).trim() === "") {
      toast.error(`${config.label || path} is required`);

      if (config.step) {
        setStep(config.step);
        window.scrollTo({
          top: sectionRef.current?.offsetTop - 100,
          behavior: "smooth"
        });
      }

      return;
    }
  }

  // 3. Agreements check
  if (!allAgreementsChecked) {
    toast.error("Please accept all declarations");
    return;
  }

  // 4. Submit
  setIsSubmitting(true);

try {

await Promise.all([
  submitApplication(),
]);


  hasCreatedRef.current = false;
  sessionStorage.removeItem("applicationId");

  setFormData({});
  setStep(1);

  toast.success("Application submitted successfully");
} catch (err) {
  toast.error(err?.message || "Submission failed");
} finally {
  setIsSubmitting(false);
}
};

  return (
    <FormContext.Provider value={{ registerField, setInvalidFields }}>
            <div ref={sectionRef} className="min-h-screen bg-white pt-24 pb-20">
      <Toaster position="top-right" richColors />
      <div className="container mx-auto px-4 lg:px-8">
        {/* Progress Stepper */}
        <div className="mb-12 max-w-5xl mx-auto">
          <div className="flex justify-between items-center relative">
            <div className="absolute left-0 right-0 top-1/2 h-1 bg-gray-200 -z-10 rounded-full">
              <div className="h-full bg-primary transition-all duration-500 rounded-full" style={{ width: `${((step - 1) / 4) * 100}%` }}></div>
            </div>
            {[{ num: 1, label: "Personal" }, { num: 2, label: "Address" }, { num: 3, label: "Employment" }, { num: 4, label: "Expenses" }, { num: 5, label: "Declarations" }].map((s) => (
              <div key={s.num} className="flex flex-col items-center">
                <button onClick={() => setStep(s.num)} className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-300 border-4 cursor-pointer focus:outline-none ${step >= s.num ? 'bg-primary border-white text-white shadow-lg' : 'bg-white border-gray-200 text-gray-400'}`}>
                  {step > s.num ? <FaCheck /> : s.num}
                </button>
                <span className={`mt-2 text-xs font-bold uppercase tracking-wider hidden sm:block ${step === s.num ? 'text-primary' : 'text-gray-400'}`}>{s.label}</span>
              </div>
            ))}
          </div>
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-7xl mx-auto">
          <div className="bg-white rounded-3xl p-6 md:p-12 border border-gray-100 shadow-xl">
            <div className="mb-10 border-b border-gray-100 pb-6">
              <h2 className="font-display text-xl font-semibold text-dark">
                {step === 1 && "Personal Information"}
                {step === 2 && "Address & Identification"}
                {step === 3 && "Employment Details"}
                {step === 4 && "Financial Overview"}
                {step === 5 && "Notes & Declarations"}
              </h2>
              <p className="text-gray-500 mt-2 text-base">
                {step === 1 && "Let's start with your basic details and loan request."}
                {step === 2 && "We need your current address and ID for verification."}
                {step === 3 && "Tell us about your current employment situation."}
                {step === 4 && "A quick overview of your income and expenses."}
                {step === 5 && "Add any final notes and confirm the declarations below to submit your application."}
              </p>
            </div>

            {/* STEP 1 */}
            <StepContext.Provider value={1}>
            <div className={step === 1 ? "space-y-8" : "hidden"}>
              <div className="grid md:grid-cols-3 gap-8">
                <FormSelect label="Title" required path="basicDetails.personalDetails.title" formData={formData} setFormData={setFormData} options={[{ value: "", label: "Select" }, { value: "Mr", label: "Mr" }, { value: "Mrs", label: "Mrs" }, { value: "Ms", label: "Ms" }]} />
                <FormInput label="First Name" required path="basicDetails.personalDetails.firstName" formData={formData} setFormData={setFormData} placeholder="First name" />
                <FormInput label="Last Name" required path="basicDetails.personalDetails.lastName" formData={formData} setFormData={setFormData} placeholder="Last name" />
              </div>
              <div className="grid md:grid-cols-3 gap-8">
                <FormInput label="Email Address" required path="basicDetails.contactDetails.email" type="email" formData={formData} setFormData={setFormData} validate={(val) =>   /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val) } placeholder="your@email.com" />
                <FormInput label="Phone Number" required path="basicDetails.contactDetails.phone.0.number"  formData={formData} setFormData={setFormData} validate={validatePhone} transform={formatPhone} placeholder="0400 000 000" />
                <FormInput label="Date of Birth" required path="basicDetails.personalDetails.dateOfBirth" isDate formData={formData} setFormData={setFormData} validate={validateDate} transform={transformDate} placeholder="DD-MM-YYYY" />
              </div>
              <div className="grid md:grid-cols-3 gap-8">
                <FormSelect label="Gender" required path="basicDetails.personalDetails.gender" formData={formData} setFormData={setFormData} options={[{ value: "", label: "Select", disabled: true }, { value: "Male", label: "Male" }, { value: "Female", label: "Female" }, { value: "Non-binary", label: "Non Binary" }, { value: "Prefer not to say", label: "Prefer not to say" }]} />
                <FormSelect label="Marital Status" required path="basicDetails.personalDetails.maritalStatus" formData={formData} setFormData={setFormData} options={[{ value: "", label: "Select", disabled: true }, { value: "Single", label: "Single" }, { value: "Married", label: "Married" }, { value: "Divorced", label: "Divorced" }]} />
                </div>
                {/* <FormSelect label="Add Company Details?" path="companyToggle" formData={{companyToggle: showCompanyDetails ? "yes" : "no"}} setFormData={setFormData} onChangeOverride={(v) => setShowCompanyDetails(v === "yes")} options={[{ value: "no", label: "No" }, { value: "yes", label: "Yes" }]} />
              
              {showCompanyDetails && (
                <div className="grid md:grid-cols-3 gap-8 animate-fadeIn">
                  <FormInput label="Company Name" path="basicDetails.companyDetails.companyName" formData={formData} setFormData={setFormData} />
                  <FormInput label="ABN" path="basicDetails.companyDetails.abn" formData={formData} setFormData={setFormData} />
                  <FormInput label="ACN" path="basicDetails.companyDetails.acn" formData={formData} setFormData={setFormData} />
                </div>
              )} */}
              <h3 className="font-display text-xl font-semibold mt-8 text-gray-800">Loan Request Details</h3>
              <div className="grid md:grid-cols-3 gap-8">
                
                {/* <FormSelect label="Type Of Loan" required path="loanDetails.type" formData={formData} setFormData={setFormData} options={[{ value: "", label: "Select Type", disabled: true }, { value: "Personal Loan", label: "Personal Loan" }, { value: "Business Loan", label: "Business Loan" }]} /> */}
                <FormInput label="Loan Amount ($)" required path="loanDetails.amount" type="number" formData={formData} setFormData={setFormData} placeholder="5000" />
                <FormInput label="Loan Term (Weeks)" required path="loanDetails.term.weeks" type="number" formData={formData} setFormData={setFormData} defaultValue="8" placeholder="e.g. 8" />
                <FormSelect label="Primary Purpose" required path="loanDetails.purpose" formData={formData} setFormData={setFormData} options={[{ value: "", label: "Select Purpose", disabled: true }, { value: "debt_consolidation", label: "Debt Consolidation" }, { value: "education_bills", label: "Education Expenses" }, { value: "family_assistance", label: "Family Assistance" }, { value: "funeral_expenses", label: "Funeral Expenses" }, { value: "home_improvement", label: "Home Improvement" }, { value: "household_goods", label: "Household Goods or Furniture" }, { value: "medical_bills", label: "Medical Bills" }, { value: "non_utility_bills", label: "Non Utility Bills" }, { value: "once_off_purchase", label: "Once off Purchase" }, { value: "family_expenses", label: "Once-off Family Expenses" }, { value: "other", label: "Other" }, { value: "rent_bond_moving", label: "Rent or Bond or Moving Expenses" }, { value: "special_occasion", label: "Special Occasion" }, { value: "travel_entertainment", label: "Travel or Entertainment" }, { value: "vehicle_expenses", label: "Vehicle Expenses" }]} />
                <FormInput label="Secondary Purpose" path="loanDetails.secondaryPurpose" formData={formData} setFormData={setFormData} placeholder="Any specific details?" />
              </div>
            </div></StepContext.Provider>

            {/* STEP 2 */}
            <StepContext.Provider value={2}>
            <div className={step === 2 ? "space-y-8" : "hidden"}>
              <div className="grid md:grid-cols-3 gap-8">
                <FormSelect label="Address Type" required path="basicDetails.contactDetails.address.0.type" formData={formData} setFormData={setFormData} options={[{ value: "", label: "Select", disabled: true },{ value: "Home", label: "Home" }, { value: "Work", label: "Work" }, { value: "Postal", label: "Postal" }]} />
                <FormInput label="Street Address" required path="basicDetails.contactDetails.address.0.street" formData={formData} setFormData={setFormData} placeholder="123 Main St" />
                <FormInput label="Suburb" required path="basicDetails.contactDetails.address.0.suburb" formData={formData} setFormData={setFormData} />
              </div>
              <div className="grid md:grid-cols-3 gap-8">
                <FormSelect label="State" required path="basicDetails.contactDetails.address.0.state" formData={formData} setFormData={setFormData} options={[{ value: "", label: "Select", disabled: true }, { value: "NSW", label: "NSW" }, { value: "VIC", label: "VIC" }, { value: "QLD", label: "QLD" }, { value: "WA", label: "WA" }, { value: "SA", label: "SA" }, { value: "TAS", label: "TAS" }, { value: "ACT", label: "ACT" }, { value: "NT", label: "NT" }]} />
                <FormInput label="Postcode" required path="basicDetails.contactDetails.address.0.postcode" formData={formData} setFormData={setFormData} validate={(val)=> /^\d{0,4}$/.test(val)}/>
                <FormInput label="Country" required path="basicDetails.contactDetails.address.0.country" formData={formData} setFormData={setFormData} defaultValue="Australia" placeholder="Australia" />
              </div>
              <div className="grid md:grid-cols-3 gap-8">
                <FormInput label="Time at Address" required path="basicDetails.contactDetails.address.0.periodAtAddress" formData={formData} setFormData={setFormData} placeholder="e.g. 2" />
                <FormSelect label="Preferred Contact Mode" path="basicDetails.contactDetails.contactMethodPreference" formData={formData} setFormData={setFormData} options={[{ value: "email", label: "Email" }, { value: "phone", label: "Phone" }, { value: "sms", label: "SMS" }]} />
              </div>
            </div></StepContext.Provider>

            {/* STEP 3 */}
            <StepContext.Provider value={3}>
            <div className={step === 3 ? "space-y-8" : "hidden"}>
              <div className="grid md:grid-cols-3 gap-8">
                <FormSelect label="Employment Status" required path="basicDetails.employmentDetails.employmentStatus" formData={formData} setFormData={setFormData} options={[{ value: "", label: "Select", disabled: true }, { value: "Full-Time", label: "Full-Time" }, { value: "Part-Time", label: "Part-Time" }, { value: "Casual", label: "Casual" }]} />
                <FormInput label="Company Name" path="basicDetails.employmentDetails.companyName" formData={formData} setFormData={setFormData} />
                <FormInput label="Position / Job Title" path="basicDetails.employmentDetails.position" formData={formData} setFormData={setFormData} />
              </div>
              <div className="grid md:grid-cols-3 gap-8">
                <FormInput label="Start Date" path="basicDetails.employmentDetails.employmentDate" isDate formData={formData} setFormData={setFormData} validate={validateDateValue} transform={transformDate} placeholder="DD-MM-YYYY" />
                <FormInput label="Employer Contact Name" path="basicDetails.employmentDetails.employerContactName" formData={formData} setFormData={setFormData} />
                <FormInput label="Employer Phone" path="basicDetails.employmentDetails.employerContactNumber"  formData={formData} validate={validatePhone} transform={formatPhone} setFormData={setFormData} />
              </div>
              <h4 className="font-bold text-gray-700 mt-8">Employer Address</h4>
              <div className="grid md:grid-cols-3 gap-8">
                <FormInput label="Street" path="basicDetails.employmentDetails.employerAddress.street" formData={formData} setFormData={setFormData} />
                <FormInput label="Suburb" path="basicDetails.employmentDetails.employerAddress.suburb" formData={formData} setFormData={setFormData} />
                <FormSelect label="State" path="basicDetails.employmentDetails.employerAddress.state" formData={formData} setFormData={setFormData} options={[{ value: "NSW", label: "NSW" }, { value: "VIC", label: "VIC" }, { value: "QLD", label: "QLD" }]} />
                <FormInput label="Postcode" path="basicDetails.employmentDetails.employerAddress.postcode" formData={formData} validate={(val)=> /^\d{0,4}$/.test(val)} setFormData={setFormData} />
              </div>
              <h3 className="font-display text-xl font-semibold mt-8 text-gray-800">Personal Reference</h3>
              <div className="grid md:grid-cols-3 gap-8">
                <FormSelect label="Relationship" path="basicDetails.referenceDetails.selectedRelationshipType" formData={formData} setFormData={setFormData} options={[{ value: "", label: "Select", disabled: true }, { value: "Family Member", label: "Family Member" }, { value: "Friend", label: "Friend" }, { value: "Colleague", label: "Colleague" }]} />
                <FormInput label="First Name" path="basicDetails.referenceDetails.additionalReferees.0.firstName" formData={formData} setFormData={setFormData} />
                <FormInput label="Last Name" path="basicDetails.referenceDetails.additionalReferees.0.lastName" formData={formData} setFormData={setFormData} />
              </div>
              <div className="grid md:grid-cols-3 gap-8">
                <FormInput label="Phone Number" path="basicDetails.referenceDetails.additionalReferees.0.phoneNumber"  formData={formData} validate={validatePhone} transform={formatPhone} setFormData={setFormData} />
                <FormInput label="Reference Street" path="basicDetails.referenceDetails.additionalReferees.0.address.0.street" formData={formData} setFormData={setFormData} />
                <FormInput label="Reference Suburb" path="basicDetails.referenceDetails.additionalReferees.0.address.0.suburb" formData={formData} setFormData={setFormData} />
              </div>
            </div></StepContext.Provider>

            {/* STEP 4 */}
            <StepContext.Provider value={4}>
            <div className={step === 4 ? "space-y-8" : "hidden"}>
              <p className="text-primary-700 text-center mb-6 font-semibold font-display bg-secondary-400 p-4 rounded-xl border border-secondary-700">
                Please provide estimates of your regular income and expenses.
              </p>
              <div className="grid md:grid-cols-3 gap-8">
                <FormExpense label="Net Income" required fieldKey="netIncome" formData={formData} setFormData={setFormData} />
                <FormExpense label="Rent or Mortgage" required fieldKey="rentMortgage" formData={formData} setFormData={setFormData} />
                <FormExpense label="Utilities" fieldKey="utilities" formData={formData} setFormData={setFormData} />
              </div>
              <div className="grid md:grid-cols-3 gap-8">
                <FormExpense label="Groceries" fieldKey="groceries" formData={formData} setFormData={setFormData} />
                <FormExpense label="Transport" fieldKey="transport" formData={formData} setFormData={setFormData} />
                <FormExpense label="Clothing" fieldKey="clothing" formData={formData} setFormData={setFormData} />
              </div>
              <div className="grid md:grid-cols-3 gap-8">
                <FormExpense label="Insurances" fieldKey="insurances" formData={formData} setFormData={setFormData} />
                <FormExpense label="Recreation" fieldKey="recreation" formData={formData} setFormData={setFormData} />
                <FormExpense label="Others" fieldKey="others" formData={formData} setFormData={setFormData} />
              </div>
              <div className="flex justify-center mt-8">
                <button id='talefin-bankstatement' onClick={openBankModal} className="bg-primary hover:bg-primary-700 text-white py-4 px-10 rounded-full flex items-center gap-3 font-bold shadow-lg transition-all">
                  <span>Verify Bank Statement</span> <BiSolidBank />
                </button>
              </div>
            </div></StepContext.Provider>

            {/* STEP 5 */}
            <StepContext.Provider value={5}>
            <div className={step === 5 ? "space-y-8" : "hidden"}>
              <div className="mt-8">
                <label className="block text-gray-700 font-semibold mb-3">Additional Notes</label>
                <textarea className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all resize-none" rows={4} value={note} onChange={(e) => setNote(e.target.value)} />
              </div>
              <div className="mt-8 space-y-4 border-t border-gray-100 pt-6">
                <label className="flex items-start gap-3 cursor-pointer flex-shrink-0">
                  <input type="checkbox" checked={agreements.confirmAccuracy} onChange={() => toggleAgreement("confirmAccuracy")} className="mt-1 w-4 h-4 accent-primary" />
                  <span className="text-gray-700 leading-relaxed block text-left">I confirm that the information I have given is true and accurate.</span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer flex-shrink-0">
                  <input type="checkbox" checked={agreements.confirmAuthorised} onChange={() => toggleAgreement("confirmAuthorised")} className="w-4 h-4 mt-1 accent-primary" />
                  <span className="text-gray-700 leading-relaxed block text-left">I confirm that I am authorised to provide the personal details presented. I consent to my information being checked with the document issuer or official record holder via third party systems for the purpose of confirming my identity.</span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer flex-shrink-0">
                  <input type="checkbox" checked={agreements.agreePrivacy} onChange={() => toggleAgreement("agreePrivacy")} className="mt-1 w-4 h-4 accent-primary" />
                  <span className="text-gray-700 leading-relaxed block text-left">I agree and consent to the <a href="/legal/privacy-statement" target="_blank" className="text-primary underline">Privacy Statement</a>.</span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer flex-shrink-0">
                  <input type="checkbox" checked={agreements.understandCreditInfo} onChange={() => toggleAgreement("understandCreditInfo")} className="mt-1 w-4 h-4 accent-primary" />
                  <span className="text-gray-700 leading-relaxed block text-left">I have read and understand the <a href="/legal/credit-reporting" target="_blank" className="text-primary underline">Credit Reporting Guide</a>.</span>
                </label>
              </div>
            </div></StepContext.Provider>

            {/* Navigation Buttons */}
            <div className="flex justify-between items-center mt-12 pt-8 border-t border-gray-100">
              {step > 1 ? (
                <button onClick={() => setStep(step - 1)} className="flex items-center gap-2 px-6 py-3 rounded-full font-bold text-gray-500 hover:text-primary transition-all">
                  <FaChevronLeft /> <span>Back</span>
                </button>
              ) : <div />}
              {step < 5 ? (
                <button onClick={handleNextStep} className="bg-primary text-white font-bold py-4 px-10 rounded-full flex items-center gap-3 shadow-lg">
                  <span>Continue</span> <FaChevronRight />
                </button>
              ) : (
                <button onClick={handleFinalSubmit} disabled={isSubmitting || !allAgreementsChecked} className="bg-primary-700 text-white font-bold py-4 px-10 rounded-full flex items-center gap-3 shadow-lg disabled:opacity-50">
                  <span>{isSubmitting ? 'Submitting...' : 'Submit Application'}</span>
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
    {showBankModal && (
  <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center">
    
    {/* Container */}
    <div className="relative w-full h-full md:w-[420px] md:h-[90vh] pt-12 bg-white rounded-none md:rounded-2xl overflow-hidden shadow-2xl">

      {/* Close button */}
      <button
        onClick={closeBankModal}
        className="absolute top-3 right-3 z-10 bg-white/90 hover:bg-white text-gray-700 rounded-full w-8 h-8 flex items-center justify-center shadow"
      >
        ✕
      </button>

      <div className="w-full h-full flex items-center justify-center bg-white text-gray-400">
        <iframe
        title='Talefin'
  src={iframeUrl}
  onLoad={createBankStatement}
  className="w-full h-full border-0"
/>
      </div>

    </div>
  </div>
)}
    </FormContext.Provider>
  );
};

export default ApplicationForm;
